import { useState, useRef, useEffect, cloneElement } from 'react';
import { createPortal } from 'react-dom';

/**
 * Tooltip próprio — sem delay nativo do `title=` HTML (que demora ~1s).
 * Padrão Centrão (sistema-marketing → portal-arquitetos).
 *
 * Características:
 *  - Aparece imediatamente no mouseEnter/focus, some no mouseLeave/blur
 *  - Usa `createPortal` + `position: fixed` pra escapar de overflow:hidden de cards/tabelas
 *  - Tenta posicionar acima; vira pra baixo se não couber
 *  - Em mobile (touch), alterna no toque e fecha clicando fora
 *  - Não envolve o filho em wrapper — usa `cloneElement` (não quebra layouts grid/flex)
 *
 * Uso:
 *   <Tooltip texto="Mensagem informativa"><i className="bi bi-info-circle" /></Tooltip>
 *   <Tooltip texto={null}>{<elemento/>}</Tooltip>  // sem texto, renderiza só o filho
 */
export default function Tooltip({ texto, largura = 280, children }) {
  const [visivel, setVisivel] = useState(false);
  const [pos, setPos] = useState({ left: 0, top: 0, baixo: false });
  const refAlvo = useRef(null);

  const recalcular = () => {
    if (!refAlvo.current) return;
    const r = refAlvo.current.getBoundingClientRect();
    const centroX = r.left + r.width / 2;
    const espacoAcima = r.top;
    const baixo = espacoAcima < 60;          // sem espaço em cima → mostra abaixo
    setPos({
      left: Math.max(8, Math.min(centroX - largura / 2, window.innerWidth - largura - 8)),
      top: baixo ? r.bottom + 8 : r.top - 8,
      baixo,
    });
  };

  // Fecha clicando/tocando fora
  useEffect(() => {
    if (!visivel) return;
    const fechaFora = (e) => {
      if (refAlvo.current && !refAlvo.current.contains(e.target)) setVisivel(false);
    };
    document.addEventListener('mousedown', fechaFora);
    document.addEventListener('touchstart', fechaFora, { passive: true });
    return () => {
      document.removeEventListener('mousedown', fechaFora);
      document.removeEventListener('touchstart', fechaFora);
    };
  }, [visivel]);

  if (!texto) return children;

  // Anexa handlers no FILHO (sem wrapper) usando cloneElement
  const filhoOrig = children;
  const ourHandlers = {
    ref: (n) => { refAlvo.current = n; },
    onMouseEnter: (e) => { recalcular(); setVisivel(true); filhoOrig.props.onMouseEnter?.(e); },
    onMouseLeave: (e) => { setVisivel(false); filhoOrig.props.onMouseLeave?.(e); },
    onFocus:     (e) => { recalcular(); setVisivel(true); filhoOrig.props.onFocus?.(e); },
    onBlur:      (e) => { setVisivel(false); filhoOrig.props.onBlur?.(e); },
    onClick:     (e) => {
      // Em touch (sem mouseenter), o click alterna o tooltip
      const touch = window.matchMedia('(hover: none)').matches;
      if (touch) {
        e.preventDefault();
        if (!visivel) recalcular();
        setVisivel(v => !v);
      }
      filhoOrig.props.onClick?.(e);
    },
  };

  const filhoClonado = cloneElement(filhoOrig, ourHandlers);

  return (
    <>
      {filhoClonado}
      {visivel && createPortal(
        <div
          className="tooltip-arq"
          style={{
            left: pos.left,
            top: pos.top,
            width: largura,
            transform: pos.baixo ? 'translateY(0)' : 'translateY(-100%)',
          }}
          role="tooltip"
        >
          {texto}
        </div>,
        document.body,
      )}
    </>
  );
}
